import {
  clearStatus,
  hideError,
  requestStatus,
  showErrorMessage,
} from '../../actions/global';
import {
  REPORT,
  TICKET_OUT,
  TRANSACTION,
} from '../../../components/global/Server/endpoints';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../pages/Dashboard/constants';
import {
  DELETE_TICKET_OUT,
  DELETE_TRANSACTION,
  GLOBAL_PDF_PREVIEW,
  GET_TICKET_PHOTO_SETTING,
  SAVE_TICKET_PHOTO_SETTING,
  LIST_ALL_TICKET_OUT,
  LIST_ALL_TRANSACTION,
  ROLLOVER_REQUEST_TRANSACTION,
  ROLLOVER_TRANSACTION,
  SAVE_CASH_LEFT_DETAILS,
  SAVE_TICKET_OUT,
  SAVE_TRANSACTION,
} from '../../constants/ticketOut';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { userInfoSelector } from '../../selectors/auth';

import {
  storeAllRolloverRequest,
  storeAllTicketOut,
  storeAllTransaction,
  storeGlobalPDFPreviewReady,
  storeReportPhotos,
  storeTicketPhotoSetting,
  storeUpdatedTicketOut,
} from '../../actions/ticketOut';
import { HTTP_SUCCESS } from '../../constants/global';
import AuthUtil from '../../AuthUtil';

function* listTicketOutSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(TICKET_OUT.LIST_ALL_TICKET_OUT, action.data)
    );
    if (response.data.success) {
      yield put(storeAllTicketOut(response.data.ticketOut));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* deleteTicketOutSaga(action) {
  yield put(hideError());
  try {
    // actingUserId: the navbar-switched employee, so the audit trail names
    // who deleted it (the token belongs to the device login).
    const user = yield select(userInfoSelector);
    const data = { ...action.data, actingUserId: user?.userId };
    const response = yield call(() =>
      AuthUtil.postRequest(TICKET_OUT.DELETE_TICKET_OUT, data)
    );
    if (response.data.success) {
      yield put(storeAllTicketOut(response.data.ticketOut));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* saveTicketOutSaga(action) {
  yield put(hideError());
  try {
    // When a photo is attached (high-value ticket), send multipart with
    // the JSON fields in a "data" part and the image in a "photo" file.
    const { photo, ...fields } = action.data || {};
    let response;
    if (photo) {
      const formData = new FormData();
      formData.append('data', JSON.stringify(fields));
      formData.append('photo', photo, photo.name);
      response = yield call(() =>
        AuthUtil.postMultipartRequest(TICKET_OUT.SAVE_TICKET_OUT, formData)
      );
    } else {
      response = yield call(() =>
        AuthUtil.postRequest(TICKET_OUT.SAVE_TICKET_OUT, fields)
      );
    }
    if (response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }
      yield put(storeUpdatedTicketOut(response.data.updatedTicket));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getTicketPhotoSettingSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(TICKET_OUT.GET_PHOTO_SETTING, action.data)
    );
    if (response.data.success) {
      yield put(storeTicketPhotoSetting(response.data.setting));
    }
  } catch (error) {
    // Non-fatal: the UI falls back to the default setting already in state.
  }
}

function* saveTicketPhotoSettingSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(TICKET_OUT.SAVE_PHOTO_SETTING, action.data)
    );
    if (response.data.success) {
      yield put(storeTicketPhotoSetting(response.data.setting));
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Settings saved');
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

//Transaction
function* listTransactionSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(TRANSACTION.LIST_ALL_TRANSACTION, action.data)
    );
    if (response.data.success) {
      yield put(storeAllTransaction(response.data.transactions));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* deleteTransactionSaga(action) {
  yield put(hideError());
  try {
    // actingUserId: see deleteTicketOutSaga.
    const user = yield select(userInfoSelector);
    const data = { ...action.data, actingUserId: user?.userId };
    const response = yield call(() =>
      AuthUtil.postRequest(TRANSACTION.DELETE_TRANSACTION, data)
    );
    if (response.data.success) {
      yield put(storeAllTransaction(response.data.transactions));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* saveTransactionSaga(action) {
  yield put(hideError());
  try {
    // When a photo is attached ("EXTRA MATCH / LUCKY"), send multipart with
    // the JSON fields in a "data" part and the image in a "photo" file part
    // — same shape as saveTicketOutSaga. Otherwise fall back to plain JSON.
    const { photo, ...fields } = action.data || {};
    let response;
    if (photo) {
      const formData = new FormData();
      formData.append('data', JSON.stringify(fields));
      formData.append('photo', photo, photo.name);
      response = yield call(() =>
        AuthUtil.postMultipartRequest(TRANSACTION.SAVE_TRANSACTION, formData)
      );
    } else {
      response = yield call(() =>
        AuthUtil.postRequest(TRANSACTION.SAVE_TRANSACTION, fields)
      );
    }
    if (response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }
      yield put(storeAllTransaction(response.data.transactions));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* saveCashLeftSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(TRANSACTION.CASH_LEFT, action.data)
    );
    if (response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Cash Left Saved');
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* rollOverTransactionSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(TRANSACTION.ROLLOVER, action.data)
    );
    if (response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(
          null,
          response.data.message
            ? response.data.message
            : 'Rollover request sent'
        );
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* rolloverRequestListSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(TRANSACTION.ROLLOVER_REQUEST, action.data)
    );

    if (response.data.success) {
      yield put(storeAllRolloverRequest(response.data.transactions));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* globalReportPreviewSaga(action) {
  yield call(clearStatus);
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(
        `${REPORT.GLOBAL_PDF}${action.intent}`,
        action.pdfPreview
      )
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    if (response.data.success) {
      const { pdfData, reportPhotos } = response.data;

      yield put(storeGlobalPDFPreviewReady(Uint8Array.from(pdfData)));

      if (reportPhotos) {
        yield put(storeReportPhotos(reportPhotos));
      } else {
        yield put(storeReportPhotos([]));
      }
    } else {
      yield put(storeGlobalPDFPreviewReady(new Uint8Array(0)));
      yield put(showErrorMessage(response.data.message));
    }
    yield put(requestStatus(status));
  } catch (e) {
    yield put(showErrorMessage('Failed to load PDF'));
    yield put(storeGlobalPDFPreviewReady(new Uint8Array(0)));
  }
}

export default function* ticketOutSagas() {
  yield takeLatest(LIST_ALL_TICKET_OUT, listTicketOutSaga);
  yield takeLatest(DELETE_TICKET_OUT, deleteTicketOutSaga);
  yield takeLatest(SAVE_TICKET_OUT, saveTicketOutSaga);
  yield takeLatest(GET_TICKET_PHOTO_SETTING, getTicketPhotoSettingSaga);
  yield takeLatest(SAVE_TICKET_PHOTO_SETTING, saveTicketPhotoSettingSaga);
  //Transaction
  yield takeLatest(LIST_ALL_TRANSACTION, listTransactionSaga);
  yield takeLatest(DELETE_TRANSACTION, deleteTransactionSaga);
  yield takeLatest(SAVE_TRANSACTION, saveTransactionSaga);
  yield takeLatest(SAVE_CASH_LEFT_DETAILS, saveCashLeftSaga);
  yield takeLatest(ROLLOVER_TRANSACTION, rollOverTransactionSaga);
  yield takeLatest(ROLLOVER_REQUEST_TRANSACTION, rolloverRequestListSaga);
  //Global Report
  yield takeLatest(GLOBAL_PDF_PREVIEW, globalReportPreviewSaga);
}
